import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as Icons from "lucide-react";
import { EditRecordButton } from "@/components/explorer/RecordEditor";

// Reusable record table with live search, column sort and optional filters.
// columns: [{ key, label, render?(row), sortValue?(row), className? }]
// filters: [{ key, label, options:[{value,label}], predicate(row,value) }]
export function RecordTable({ rows, columns, searchKeys, filters = [], emptyText = "No records.", testId = "record-table", path }) {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [dir, setDir] = useState("asc");
  const [fv, setFv] = useState({});

  const val = (row, c) => (c.sortValue ? c.sortValue(row) : row[c.key]);

  const data = useMemo(() => {
    let d = rows;
    filters.forEach((f) => { const v = fv[f.key] || "all"; if (v !== "all") d = d.filter((r) => f.predicate(r, v)); });
    if (q) {
      const s = q.toLowerCase();
      d = d.filter((r) => (searchKeys ? searchKeys.map((k) => r[k]).join(" ") : JSON.stringify(r)).toLowerCase().includes(s));
    }
    if (sortKey) {
      const c = columns.find((x) => x.key === sortKey);
      if (c) d = [...d].sort((a, b) => {
        let x = val(a, c), y = val(b, c);
        if (typeof x === "number" && typeof y === "number") return dir === "asc" ? x - y : y - x;
        x = x == null ? "" : String(x); y = y == null ? "" : String(y);
        return dir === "asc" ? x.localeCompare(y) : y.localeCompare(x);
      });
    }
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, q, sortKey, dir, JSON.stringify(fv)]);

  const toggleSort = (k) => { if (sortKey === k) setDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setDir("asc"); } };

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid={`${testId}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search records…" className="pl-9 h-9" />
        </div>
        {filters.map((f) => (
          <Select key={f.key} value={fv[f.key] || "all"} onValueChange={(v) => setFv((s) => ({ ...s, [f.key]: v }))}>
            <SelectTrigger className="h-9 w-auto min-w-[130px]" data-testid={`${testId}-filter-${f.key}`}><SelectValue placeholder={f.label} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{f.label}: All</SelectItem>
              {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        <span className="text-xs text-muted-foreground font-mono ml-auto">Showing {data.length} record(s)</span>
      </div>
      <div className="rounded-lg border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key} className={c.className}>
                  {c.sortable === false ? c.label : (
                    <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`${testId}-sort-${c.key}`}>
                      {c.label}
                      {sortKey === c.key ? (dir === "asc" ? <Icons.ArrowUp className="h-3 w-3" /> : <Icons.ArrowDown className="h-3 w-3" />) : <Icons.ChevronsUpDown className="h-3 w-3 opacity-40" />}
                    </button>
                  )}
                </TableHead>
              ))}
              {path && <TableHead className="w-8" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.slice(0, 500).map((row, i) => (
              <TableRow key={row.id || i} data-testid={`${testId}-row-${row.id || i}`}>
                {columns.map((c) => <TableCell key={c.key} className={c.className}>{c.render ? c.render(row) : (row[c.key] ?? "—")}</TableCell>)}
                {path && <TableCell className="w-8"><EditRecordButton path={path} id={row.id} /></TableCell>}
              </TableRow>
            ))}
            {data.length === 0 && <TableRow><TableCell colSpan={columns.length} className="text-center py-10 text-muted-foreground">{emptyText}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
